import { useMutation, useQueryClient } from "react-query";
import { uploadUserAvatar } from "api/userAvatar";
import { GET_USER_BY_ID, GET_TEAM_MEMBERS } from "@constants/reactQueryKeys";

interface UploadAvatarParams {
  userId: number;
  file: File;
  cropParams?: string;
}

interface UploadAvatarResponse {
  success: boolean;
  imageUrl: string;
  userId: number;
}

interface UseUploadUserAvatarOptions {
  onSuccess?: (data: UploadAvatarResponse, variables: UploadAvatarParams) => void;
  onError?: (error: Error) => void;
}

export const useUploadUserAvatar = (options: UseUploadUserAvatarOptions = {}) => {
  const queryClient = useQueryClient();

  const {
    mutate: uploadAvatar,
    mutateAsync: uploadAvatarAsync,
    isLoading,
    isError,
    error,
    isSuccess,
    data,
    ...rest
  } = useMutation(
    ({ userId, file, cropParams }: UploadAvatarParams) => uploadUserAvatar({ userId, file, cropParams }),
    {
      onSuccess: (response, variables) => {
        queryClient.invalidateQueries([GET_USER_BY_ID, variables.userId]);
        queryClient.invalidateQueries([GET_TEAM_MEMBERS]);

        queryClient.setQueryData([GET_USER_BY_ID, variables.userId], (oldData: any) => {
          if (!oldData?.data) return oldData;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              imageUrl: response.data.imageUrl,
            },
          };
        });

        if (options.onSuccess) {
          options.onSuccess(response.data, variables);
        }
      },
      onError: (error: Error) => {
        console.error("Error uploading user avatar:", error);
        
        if (options.onError) {
          options.onError(error);
        }
      },
    }
  );

  return {
    uploadAvatar,
    uploadAvatarAsync,
    isUploading: isLoading,
    isError,
    error,
    isSuccess,
    data,
    ...rest,
  };
};
